/** @typedef {import('./parseIcs.js').LmsCalendarEvent} LmsCalendarEvent */

/**
 * Mid-terms, finals, generic exams/assessments — hide from Quizzes UI; show only regular quizzes.
 * @param {LmsCalendarEvent} ev
 */
function isHighStakesOrAssessmentTitle(ev) {
    const t = (ev.title || '').toLowerCase();
    if (/\bmid[\s-]?term\b|\bmidterm\b/.test(t)) return true;
    if (/\bmid[\s-]?sem\b|\bmidsem\b|\bmid\s*sem\b/.test(t)) return true;
    if (/\bmte\b/.test(t)) return true;
    if (/\bfinal\b|\bfinals\b/.test(t)) return true;
    if (/\bexam\b/.test(t)) return true;
    if (/\bassessment\b/.test(t)) return true;
    if (/\b(end[\s-]?sem|endsem|semester\s*exam)\b/.test(t)) return true;
    if (/\bcomprehensive\b|\bterm\s*paper\b/.test(t)) return true;
    return false;
}

/**
 * Regular weekly / course quizzes only (not mid-terms, finals, exams, or generic assessments).
 * @param {LmsCalendarEvent} ev
 * @returns {boolean}
 */
export function isQuizEvent(ev) {
    if (isHighStakesOrAssessmentTitle(ev)) return false;
    const t = (ev.title || '').toLowerCase();
    if (/\bquiz\b/.test(t)) return true;
    const url = `${ev.url || ''} ${ev.location || ''}`.toLowerCase();
    if (/\/quizzes\//i.test(url)) return true;
    return false;
}

/**
 * Quiz events that have not ended yet (still due or in progress).
 *
 * @param {LmsCalendarEvent[]} events
 * @param {Date} [now]
 * @returns {LmsCalendarEvent[]}
 */
export function filterUpcomingQuizEvents(events, now = new Date()) {
    const t = now.getTime();
    return events.filter((ev) => {
        if (!isQuizEvent(ev)) return false;
        return parseEventEnd(ev).getTime() >= t;
    });
}

/**
 * All LMS feed events that have not ended yet (live classes, assignments, quizzes, etc.).
 *
 * @param {LmsCalendarEvent[]} events
 * @param {Date} [now]
 * @returns {LmsCalendarEvent[]}
 */
export function filterUpcomingLmsEvents(events, now = new Date()) {
    const t = now.getTime();
    return events.filter((ev) => parseEventEnd(ev).getTime() >= t);
}

/**
 * @param {LmsCalendarEvent} ev
 * @returns {Date}
 */
export function parseEventStart(ev) {
    if (ev.allDay) {
        return new Date(`${ev.start}T00:00:00.000Z`);
    }
    const s = ev.start;
    return new Date(s.endsWith('Z') ? s : `${s}Z`);
}

/**
 * @param {LmsCalendarEvent} ev
 * @returns {Date}
 */
export function parseEventEnd(ev) {
    if (ev.end) {
        if (ev.allDay) {
            return new Date(`${ev.end}T23:59:59.999Z`);
        }
        const e = ev.end;
        return new Date(e.endsWith('Z') ? e : `${e}Z`);
    }
    if (ev.allDay) {
        return new Date(`${ev.start}T23:59:59.999Z`);
    }
    return new Date(parseEventStart(ev).getTime() + 60 * 60 * 1000);
}

/**
 * Local Saturday 00:00 through the following Sunday 23:59:59.
 * If `now` is Saturday or Sunday, uses that weekend; otherwise the next Sat–Sun.
 *
 * @param {Date} [now]
 * @returns {{ start: Date, end: Date }}
 */
export function getUpcomingWeekendLocalRange(now = new Date()) {
    const y = now.getFullYear();
    const m = now.getMonth();
    const d = now.getDate();
    const day = now.getDay();

    let satY = y;
    let satM = m;
    let satD = d;

    if (day === 6) {
        // Saturday — this weekend
    } else if (day === 0) {
        const prev = new Date(y, m, d - 1);
        satY = prev.getFullYear();
        satM = prev.getMonth();
        satD = prev.getDate();
    } else {
        const daysUntilSat = 6 - day;
        const nextSat = new Date(y, m, d + daysUntilSat);
        satY = nextSat.getFullYear();
        satM = nextSat.getMonth();
        satD = nextSat.getDate();
    }

    const start = new Date(satY, satM, satD, 0, 0, 0, 0);
    const end = new Date(satY, satM, satD + 1, 23, 59, 59, 999);
    return { start, end };
}

/**
 * Events that have not ended and start on or before the horizon end (local clock),
 * or within an optional inclusive local [rangeStart, rangeEnd] on event start.
 *
 * @param {LmsCalendarEvent[]} events
 * @param {{
 *   horizonDays?: number,
 *   maxCount?: number,
 *   now?: Date,
 *   rangeStart?: Date,
 *   rangeEnd?: Date,
 * }} [options]
 * @returns {LmsCalendarEvent[]}
 */
export function selectUpcomingEvents(events, options = {}) {
    const maxCount = options.maxCount ?? 12;
    const now = options.now ?? new Date();
    const rangeStart = options.rangeStart;
    const rangeEnd = options.rangeEnd;

    const useRange = rangeStart != null && rangeEnd != null;
    const horizonDays = options.horizonDays ?? 7;
    const horizonEnd = useRange
        ? null
        : new Date(now.getFullYear(), now.getMonth(), now.getDate() + horizonDays, 23, 59, 59, 999);

    const rows = events.map((ev) => ({
        ev,
        start: parseEventStart(ev),
        end: parseEventEnd(ev),
    }));

    const filtered = rows.filter(({ start, end }) => {
        if (end.getTime() < now.getTime()) return false;
        if (useRange && rangeStart && rangeEnd) {
            if (start.getTime() < rangeStart.getTime()) return false;
            if (start.getTime() > rangeEnd.getTime()) return false;
        } else if (horizonEnd) {
            if (start.getTime() > horizonEnd.getTime()) return false;
        }
        return true;
    });

    filtered.sort((a, b) => a.start - b.start);
    return filtered.slice(0, maxCount).map((r) => r.ev);
}

/**
 * Trailing bracket segment from VIT-style titles, e.g. "[MSC-DS-…-Weekend]".
 * @param {string} title
 * @returns {string | null}
 */
export function extractCourseBracketKey(title) {
    const m = (title || '').match(/\[([^\]]+)\]\s*$/);
    return m ? m[1].trim().toLowerCase() : null;
}

/** @param {string} [url] */
function normalizeJoinUrlForDedupe(url) {
    if (!url) return '';
    const t = url.trim();
    try {
        const u = new URL(t);
        return `${u.origin}${u.pathname}`.toLowerCase();
    } catch {
        return t.toLowerCase();
    }
}

/**
 * Local yyyy-mm-dd for an instant (matches calendar grid / dashboard “this week”).
 * @param {Date} d
 */
function localDateKeyFromDate(d) {
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${mo}-${day}`;
}

/**
 * LMS often emits "Quiz 3.1 …" and "Required Quiz 3.1 …" (or assignment vs calendar) for the same due window.
 * @param {string} title
 */
function normalizeQuizDedupeTitle(title) {
    return (title || '')
        .toLowerCase()
        .replace(/\brequired\s+/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}

/** @param {LmsCalendarEvent} ev */
function weekNumberInTitle(ev) {
    const m = (ev.title || '').match(/\bweek\s+(\d+)\b/i);
    return m ? parseInt(m[1], 10) : -1;
}

/**
 * @param {LmsCalendarEvent} ev
 * @returns {string}
 */
function lmsEventDedupeKey(ev) {
    const start = parseEventStart(ev);
    const end = parseEventEnd(ev);
    const startMs = start.getTime();
    const endMs = end.getTime();
    const normTitle = (ev.title || '').replace(/\s+/g, ' ').trim().toLowerCase();

    if (isQuizEvent(ev)) {
        return `quiz|${startMs}|${endMs}|${normalizeQuizDedupeTitle(ev.title)}`;
    }

    if (ev.kind === 'session') {
        const bracket = extractCourseBracketKey(ev.title);
        if (bracket) {
            const dayKey = localDateKeyFromDate(start);
            return `sess-day|${dayKey}|${bracket}`;
        }
        if (ev.url) {
            return `sess|${startMs}|${endMs}|u|${normalizeJoinUrlForDedupe(ev.url)}`;
        }
        return `sess|${startMs}|${endMs}|t|${normTitle}`;
    }

    return `${ev.kind}|${startMs}|${endMs}|t|${normTitle}`;
}

/**
 * @param {LmsCalendarEvent} ev
 * @returns {number}
 */
function dedupePreferenceScore(ev) {
    const t = ev.title || '';
    let s = 0;
    if (/live session:\s*week\s+\d+/i.test(t)) s += 120;
    else if (/\bweek\s+\d+\b/i.test(t)) s += 80;
    if (ev.url) s += 20;
    s += Math.min(t.length, 100);
    return s;
}

/** @param {LmsCalendarEvent} ev */
function quizDedupePreferenceScore(ev) {
    const t = ev.title || '';
    let s = Math.min(t.length, 200);
    if (/^\s*required\b/i.test(t)) s += 40;
    if (ev.kind === 'assignment') s += 15;
    if (ev.url) s += 10;
    return s;
}

/**
 * @param {string} key
 * @param {LmsCalendarEvent[]} list
 */
function pickRepresentativeEvent(key, list) {
    return list.reduce((best, ev) => {
        if (key.startsWith('sess-day|')) {
            const wEv = weekNumberInTitle(ev);
            const wBest = weekNumberInTitle(best);
            if (wEv !== wBest) return wEv > wBest ? ev : best;
        }
        const ds = key.startsWith('quiz|') ? quizDedupePreferenceScore(ev) : dedupePreferenceScore(ev);
        const bs = key.startsWith('quiz|') ? quizDedupePreferenceScore(best) : dedupePreferenceScore(best);
        if (ds > bs) return ev;
        if (ds < bs) return best;
        return (ev.uid || '') <= (best.uid || '') ? ev : best;
    });
}

/**
 * Collapse redundant LMS ICS rows: duplicate UIDs/titles, same slot + course bracket, one live class
 * per course per local day (stale Week 2 + updated Week 4), duplicate quizzes (“Required …” vs plain).
 * Preserves first-seen order in the feed.
 *
 * @param {LmsCalendarEvent[]} events
 * @returns {LmsCalendarEvent[]}
 */
export function dedupeLmsCalendarEvents(events) {
    if (!events?.length) return [];

    /** @type {Map<string, LmsCalendarEvent[]>} */
    const groups = new Map();
    /** @type {Map<string, number>} */
    const firstIndex = new Map();

    events.forEach((ev, idx) => {
        const key = lmsEventDedupeKey(ev);
        if (!groups.has(key)) {
            groups.set(key, []);
            firstIndex.set(key, idx);
        }
        groups.get(key).push(ev);
    });

    const keys = [...firstIndex.keys()].sort((a, b) => (firstIndex.get(a) ?? 0) - (firstIndex.get(b) ?? 0));

    return keys.map((key) => pickRepresentativeEvent(key, groups.get(key) ?? []));
}

export function formatEventRangeLabel(ev, locale) {
    const loc = locale ?? undefined;
    if (ev.allDay) {
        const d = new Date(`${ev.start}T12:00:00.000Z`);
        return new Intl.DateTimeFormat(loc, {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
        }).format(d);
    }
    const start = parseEventStart(ev);
    const end = ev.end && !ev.allDay ? parseEventEnd(ev) : null;
    const datePart = new Intl.DateTimeFormat(loc, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
    }).format(start);
    const timeFmt = new Intl.DateTimeFormat(loc, { hour: 'numeric', minute: '2-digit' });
    if (end && end.getTime() > start.getTime()) {
        return `${datePart} · ${timeFmt.format(start)} – ${timeFmt.format(end)}`;
    }
    return `${datePart} · ${timeFmt.format(start)}`;
}
